import { ActionIcon, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type MouseEvent } from 'react';
import { getMe, setFavorite } from '../lib/api.js';

interface Props {
  objectId: number;
  favorited: boolean;
  /** 'icon' = nur Herz (Liste/Detail) · 'label' = Herz + „Merken/Gemerkt" (Swipe-Aktionszeile). */
  variant?: 'icon' | 'label';
  /** Icon-Größe in px (nur variant 'icon'). */
  size?: number;
}

const FILLED = '#e8365d';
const EMPTY = '#94a3b8';

/**
 * Herz-Toggle für die veto-immune Merkliste. Pro Person (login_email) — deaktiviert, wenn kein
 * eigener Login vorliegt (Impersonation/alte Session), damit nichts ins Leere gemerkt wird.
 * stopPropagation, weil die Karte selbst ein Swipe-/Tap-Ziel ist.
 */
export function FavoriteHeart({ objectId, favorited, variant = 'icon', size = 22 }: Props): JSX.Element {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['auth-me'], queryFn: getMe, staleTime: 5 * 60_000 });
  const canFavorite = !!me && !me.impersonating && !!me.loginEmail;

  // Lokaler Optimistic-State (sofortiges Feedback), an das Prop synchronisiert.
  const [fav, setFav] = useState(favorited);
  useEffect(() => setFav(favorited), [favorited]);

  const mut = useMutation({
    mutationFn: (next: boolean) => setFavorite(objectId, next),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['favorites'] });
      void qc.invalidateQueries({ queryKey: ['object', objectId] });
      void qc.invalidateQueries({ queryKey: ['objects'] });
    },
    onError: () => setFav(favorited), // Rollback bei Fehler
  });

  const toggle = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    if (!canFavorite || mut.isPending) return;
    const next = !fav;
    setFav(next);
    mut.mutate(next);
  };

  const heart = fav ? '♥' : '♡';
  const color = fav ? FILLED : EMPTY;

  // Kein eigener Login → nicht-interaktives, erklärendes Herz.
  if (!canFavorite) {
    return (
      <Tooltip label="Zum Merken mit deinem eigenen Google-Login anmelden" withArrow multiline w={220}>
        <Text
          component="span"
          aria-hidden
          style={{ color: EMPTY, opacity: 0.45, fontSize: variant === 'label' ? '1.15rem' : size, lineHeight: 1, cursor: 'not-allowed' }}
        >
          ♡
        </Text>
      </Tooltip>
    );
  }

  if (variant === 'label') {
    return (
      <UnstyledButton
        onClick={toggle}
        aria-pressed={fav}
        aria-label={fav ? 'Aus Merkliste entfernen' : 'Merken'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '2px 4px' }}
      >
        <Text component="span" style={{ color, fontSize: '1.2rem', lineHeight: 1 }}>
          {heart}
        </Text>
        <Text component="span" size="sm" fw={600} c={fav ? 'pink.7' : 'dimmed'}>
          {fav ? 'Gemerkt' : 'Merken'}
        </Text>
      </UnstyledButton>
    );
  }

  return (
    <ActionIcon
      onClick={toggle}
      variant="subtle"
      color={fav ? 'pink' : 'gray'}
      size="lg"
      radius="xl"
      aria-pressed={fav}
      aria-label={fav ? 'Aus Merkliste entfernen' : 'Merken'}
    >
      <Text component="span" style={{ color, fontSize: size, lineHeight: 1 }}>
        {heart}
      </Text>
    </ActionIcon>
  );
}
